import { useState, useEffect } from "react";
import axios from "axios";
import {
  Box, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, Paper, Chip, Stack, Avatar, TextField,
  ToggleButton, ToggleButtonGroup, InputAdornment, CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const API_BASE = process.env.REACT_APP_API_DOCTOR_APPOINTMENT_URL;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
});
const TEAL = "#006D77";
const DRAWER_WIDTH = 280;

const DoctorAppointmentsAdmin = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    axios.get(`${API_BASE}/dashboard/doctor-appointments`, authHeaders())
      .then((r) => setAppointments(r.data || []))
      .catch(() => setAppointments([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = appointments.filter((a) => {
    if (filter !== "all" && a.status !== filter) return false;
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (
      a.appointment_code?.toLowerCase().includes(t) ||
      a.patient_name?.toLowerCase().includes(t) ||
      a.doctor?.name?.toLowerCase().includes(t)
    );
  });

  const confirmedCount = appointments.filter((a) => a.status !== "cancelled").length;
  const cancelledCount = appointments.filter((a) => a.status === "cancelled").length;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#EEF4F5",
        // offset for permanent drawer on desktop
        ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
        // offset for fixed mobile top bar
        mt: { xs: "56px", md: 0 },
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, sm: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Typography variant="h4" fontWeight={800} color={TEAL} gutterBottom
          sx={{ fontSize: { xs: "1.6rem", sm: "2.125rem" } }}>
          Booked Appointments
        </Typography>
        <Typography color="text.secondary" mb={3}>
          All doctor consultations booked by residents.
        </Typography>

        {/* Summary chips */}
        <Stack direction="row" spacing={1.5} mb={3} flexWrap="wrap" useFlexGap>
          <Chip label={`Total: ${appointments.length}`} sx={{ fontWeight: 700 }} />
          <Chip label={`Confirmed: ${confirmedCount}`} color="success" sx={{ fontWeight: 700 }} />
          <Chip label={`Cancelled: ${cancelledCount}`} sx={{ fontWeight: 700 }} />
        </Stack>

        {/* Search + filter */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }} flexWrap="wrap" useFlexGap mb={2}>
          <TextField size="small" placeholder="Search code / patient / doctor"
            value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: { xs: "100%", sm: 260 }, flex: { sm: "0 1 auto" } }} />
          <ToggleButtonGroup exclusive size="small" value={filter}
            onChange={(e, v) => v && setFilter(v)} sx={{ flexWrap: "wrap" }}>
            <ToggleButton value="all" sx={{ textTransform: "none" }}>All</ToggleButton>
            <ToggleButton value="confirmed" sx={{ textTransform: "none" }}>Confirmed</ToggleButton>
            <ToggleButton value="cancelled" sx={{ textTransform: "none" }}>Cancelled</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {loading ? (
          <Box textAlign="center" py={6}><CircularProgress sx={{ color: TEAL }} /></Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: "auto" }}>
            <Table sx={{ minWidth: 720 }}>
              <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Doctor</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Fee</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      <Typography fontFamily="monospace" fontWeight={700}>{a.appointment_code}</Typography>
                    </TableCell>
                    <TableCell>{a.patient_name}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar src={a.doctor?.photo_url} sx={{ width: 32, height: 32, bgcolor: TEAL, fontSize: 14 }}>
                          {a.doctor?.name?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{a.doctor?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{a.doctor?.specialization}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{a.slot?.date}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {a.slot?.start_time} - {a.slot?.end_time}
                      </Typography>
                    </TableCell>
                    <TableCell>{a.reason?.label || "—"}</TableCell>
                    <TableCell>₹{a.fee}</TableCell>
                    <TableCell>
                      <Chip size="small"
                        label={a.status === "cancelled" ? "Cancelled" : "Confirmed"}
                        color={a.status === "cancelled" ? "default" : "success"} />
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ color: "text.secondary", py: 5 }}>
                      No appointments found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default DoctorAppointmentsAdmin;